/**
 * Realtor API Service
 *
 * Profile read + update + verification submission. Listings, leads,
 * pipeline, and portfolio API surfaces live in their own services as
 * those features ship.
 *
 * Error envelope: every function returns `{ success, profile?, error?, errorCode? }`.
 * The `errorCode` mirrors the backend's structured `error` field — e.g.
 * `REALTOR_PROFILE_REQUIRED`, `REALTOR_VERIFICATION_REQUIRED`. Callers should
 * branch on `errorCode` for routing decisions (e.g., send the user to
 * onboarding vs the verification form) and fall back to `error` for the
 * human-readable toast message.
 */

import type { RealtorProfile } from "@shared/types";
import { apiClient } from "@/lib/api-client";

// Re-export for callers in apps/frontend that prefer the local path.
export type { RealtorProfile };

const API_BASE = "/api/v1";

// Subset of axios error shape we read. Backend returns
// `{ success: false, data: null, message, error }` — we surface both
// `message` (human-readable) and `error` (structured code) to callers.
interface BackendErrorBody {
	message?: string;
	error?: string;
}
interface AxiosLikeError {
	response?: { data?: BackendErrorBody; status?: number };
	code?: string;
	message?: string;
}

interface ExtractedError {
	message: string;
	errorCode: string | null;
}

/**
 * Pull the human message + structured code out of an unknown error.
 * Defends against malformed responses, network errors, and timeouts so
 * callers always get a stable shape.
 */
function extractError(error: unknown, fallback: string): ExtractedError {
	// Defensive: a thrown `undefined` or `null` is rare but possible (e.g.
	// a chained handler that resolved to nothing then re-threw). Without
	// this guard the cast below would crash with "cannot read properties
	// of undefined" and the caller would lose all error context.
	if (error == null) {
		return { message: fallback, errorCode: "UNKNOWN_ERROR" };
	}
	const err = error as AxiosLikeError;
	const body = err.response?.data;

	// Network / timeout / non-HTTP failure — no response body.
	if (!err.response) {
		const networkMessage =
			err.code === "ECONNABORTED" || err.message?.includes("timeout")
				? "Request timed out. Check your connection and try again."
				: "Couldn't reach the server. Check your connection and try again.";
		return { message: networkMessage, errorCode: "NETWORK_ERROR" };
	}

	return {
		message: body?.message || fallback,
		errorCode: body?.error ?? null,
	};
}

interface ApiResult<T> {
	success: boolean;
	error?: string;
	errorCode?: string | null;
	profile?: T;
}

function buildFailure<T>(error: unknown, fallback: string): ApiResult<T> {
	const { message, errorCode } = extractError(error, fallback);
	return { success: false, error: message, errorCode };
}

export async function getMyRealtorProfile(): Promise<{
	success: boolean;
	profile?: RealtorProfile | null;
	error?: string;
	errorCode?: string | null;
}> {
	try {
		const response = await apiClient.get<{
			data: { profile: RealtorProfile | null };
		}>(`${API_BASE}/realtors/me`);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		return buildFailure<RealtorProfile | null>(
			error,
			"Failed to load realtor profile",
		);
	}
}

export async function updateMyRealtorProfile(
	patch: Partial<{
		name: string;
		agencyName: string | null;
		bio: string | null;
		serviceCities: string[];
		serviceLocalities: string[];
		specializations: string[];
		yearsExperience: number | null;
		languages: string[];
		profileImageUrl: string | null;
		primaryCity: string | null;
		propertyTypes: string[];
		clientTypes: string[];
	}>,
): Promise<ApiResult<RealtorProfile>> {
	try {
		const response = await apiClient.patch<{
			data: { profile: RealtorProfile };
		}>(`${API_BASE}/realtors/me`, patch);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		return buildFailure<RealtorProfile>(
			error,
			"Failed to update realtor profile",
		);
	}
}

export async function submitRealtorVerification(data: {
	reraNumber: string;
	idProofUrl: string;
}): Promise<ApiResult<RealtorProfile>> {
	try {
		const response = await apiClient.post<{
			data: { profile: RealtorProfile };
		}>(`${API_BASE}/realtors/me/verification`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		return buildFailure<RealtorProfile>(error, "Failed to submit verification");
	}
}
